import React, { useState, useEffect } from 'react';
import './Pages.css';

const pad = (n) => String(n).padStart(2, '0');

// Matches the Insured Person/Vehicle/Chassis/start/end/Net columns fleet4.csv uses, so the
// export can be re-imported straight into the Create Fleet CSV upload step.
const formatCsvDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
};

const csvEscape = (value) => {
  const str = String(value == null ? '' : value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const FLEET_API_BASE = 'https://main.victriedemos.com:3017';
const FLEET_DONE_BY = 'Zakim Insurance Agency';

// Registrations may be typed with different spacing/casing on each side (policy vs fleet CSV import)
const normalizeReg = (reg) => (reg || '').toUpperCase().replace(/\s+/g, '');

const PendingPremiumSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [fleetStatusFilter, setFleetStatusFilter] = useState('');
  // Maps a normalized registration to the fleet status it was found under ('new' | 'pending' | 'approved')
  const [fleetRegistrationStatus, setFleetRegistrationStatus] = useState({});

  useEffect(() => {
    fetchProducts();
    fetchSubmissions();
    fetchFleetRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-references Declarations Due rows against every fleet's entries, so an already-imported
  // registration can be flagged (or hidden, once approved) instead of being declared twice.
  const fetchFleetRegistrations = async () => {
    try {
      const res = await fetch(`${FLEET_API_BASE}/api/staff/getMyFleets?username=${encodeURIComponent(FLEET_DONE_BY)}`);
      const data = await res.json();
      if (!data.success) return;

      const fleets = data.data || [];
      const statusMap = { 0: 'new', 2: 'pending', 3: 'approved' };

      fleets.forEach((fleet) => {
        const status = statusMap[fleet.Status];
        if (!status) return;

        fetch(`${FLEET_API_BASE}/api/staff/getFleetEntries?fleetNo=${encodeURIComponent(fleet.FleetNo)}`)
          .then((r) => r.json())
          .then((entriesData) => {
            if (!entriesData.success) return;
            setFleetRegistrationStatus(prev => {
              const next = { ...prev };
              (entriesData.data || []).forEach((entry) => {
                const reg = normalizeReg(entry.Narration);
                if (!reg) return;
                // Approved is the most "final" state - let it win if a registration somehow
                // shows up under more than one fleet.
                if (next[reg] !== 'approved') {
                  next[reg] = status;
                }
              });
              return next;
            });
          })
          .catch((err) => console.error('Error fetching entries for', fleet.FleetNo, err));
      });
    } catch (err) {
      console.error('Error fetching fleet registrations:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/products');
      const data = await response.json();
      if (data.success) {
        setProducts(data.products.filter(p => p.Status === 1));
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchSubmissions = async (from = fromDate, to = toDate, product = selectedProduct) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (product) params.append('product', product);

      const response = await fetch(`https://main.victriedemos.com:3017/api/pending-premium-submissions?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setSubmissions(data.data);
        setError('');
      } else {
        setError('Failed to load pending premium submissions');
      }
    } catch (err) {
      console.error('Error fetching pending premium submissions:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    fetchSubmissions(fromDate, toDate, selectedProduct);
  };

  const handleClearFilter = () => {
    setFromDate('');
    setToDate('');
    setSelectedProduct('');
    setFleetStatusFilter('');
    fetchSubmissions('', '', '');
  };

  const handleExportCSV = () => {
    const headers = ['Insured Person', 'Vehicle', 'Chassis', 'start', 'end', 'Net'];
    const rows = filteredSubmissions.map(s => {
      const grossPremium = parseFloat(s.GrossPremium) || 0;
      const netOfCommission = Math.round(grossPremium - (grossPremium * 0.10));
      return [
        s.Client || '',
        s.Registration || '',
        s.Chassis || '',
        formatCsvDate(s.CommencementDate),
        formatCsvDate(s.ExpiryDate),
        netOfCommission
      ];
    });

    const csvContent = [headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'declarations_due.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const filteredSubmissions = submissions
    .filter((s) => fleetRegistrationStatus[normalizeReg(s.Registration)] !== 'approved')
    .filter((s) => {
      if (!fleetStatusFilter) return true;
      const status = fleetRegistrationStatus[normalizeReg(s.Registration)];
      if (fleetStatusFilter === 'not_in_fleet') return !status;
      return status === fleetStatusFilter;
    })
    .filter((s) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      const policyNumber = (s.PolicyNumber || '').toLowerCase();
      const registration = (s.Registration || '').toLowerCase();
      const client = (s.Client || '').toLowerCase();
      return policyNumber.includes(search) || registration.includes(search) || client.includes(search);
    });

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading pending premium submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>🕒 Declarations Due</h1>
          <p className="page-subtitle">Policies and extensions approved with "Submit Premiums Later"</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="badge-count">{filteredSubmissions.length} Records</div>
          <button
            type="button"
            className="btn-primary"
            onClick={handleExportCSV}
            disabled={filteredSubmissions.length === 0}
          >
            ⬇️ Export CSV
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="search-bar">
        <input
          type="text"
          placeholder="🔍 Search by Policy Number, Registration, or Client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
            ✕ Clear
          </button>
        )}
      </div>

      <div className="date-filter-bar">
        <div className="form-group">
          <label className="form-label-icon">
            <span className="label-icon">📦</span>
            Product
          </label>
          <select
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className="form-input"
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.Id} value={p.Id}>{p.ProductName}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label-icon">
            <span className="label-icon">🚛</span>
            Fleet Status
          </label>
          <select
            value={fleetStatusFilter}
            onChange={(e) => setFleetStatusFilter(e.target.value)}
            className="form-input"
          >
            <option value="">All</option>
            <option value="not_in_fleet">Not Yet Declared</option>
            <option value="new">In New Fleet</option>
            <option value="pending">In Pending Approval</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label-icon">
            <span className="label-icon">📆</span>
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label-icon">
            <span className="label-icon">📆</span>
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="form-input"
          />
        </div>
        <button type="button" className="btn-primary" onClick={handleApplyFilter}>
          Apply Filter
        </button>
        {(fromDate || toDate || selectedProduct || fleetStatusFilter) && (
          <button type="button" className="btn-secondary" onClick={handleClearFilter}>
            Clear Filters
          </button>
        )}
      </div>

      <div className="table-container">
        {filteredSubmissions.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm || fromDate || toDate || selectedProduct || fleetStatusFilter ? 'No records match your filters' : 'No premiums pending submission'}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Registration</th>
                <th>Client</th>
                <th>Gross Premium</th>
                <th>Approved By</th>
                <th>Date Approved</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((s, index) => {
                const fleetStatus = fleetRegistrationStatus[normalizeReg(s.Registration)];
                const rowClass = fleetStatus === 'new' ? 'declaration-row-new-fleet'
                  : fleetStatus === 'pending' ? 'declaration-row-pending-fleet'
                  : '';
                return (
                <tr key={s.Id} className={rowClass}>
                  <td>{index + 1}</td>
                  <td>{s.ProductName || 'N/A'}</td>
                  <td>{s.Registration || 'N/A'}</td>
                  <td>{s.Client || 'N/A'}</td>
                  <td className="amount">{formatCurrency(s.GrossPremium)}</td>
                  <td>{s.ApprovedBy || 'N/A'}</td>
                  <td>{formatDate(s.DateApproved)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PendingPremiumSubmissions;
